import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Search } from "lucide-react";

import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/motion/Reveal";
import SectionHeading from "@/components/SectionHeading";
import CTASection from "@/components/CTASection";
import { fetchArticles } from "@/lib/api";

export default function Insights() {
  const { t, lang } = useLang();
  const ins = t.insights;
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Insights — Litterantara";
    setLoading(true);
    fetchArticles()
      .then(setArticles)
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  const categories = ["all", ...Array.from(new Set(articles.map((a) => a.category)))];
  const q = query.trim().toLowerCase();
  const filtered = articles.filter((a) => {
    if (category !== "all" && a.category !== category) return false;
    if (!q) return true;
    const hay = [a.title[lang], a.excerpt[lang], a.category, ...(a.tags || [])]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div data-testid="insights-page">
      <section className="relative pt-40 pb-12 overflow-hidden">
        <div
          className="absolute -top-20 right-1/4 w-[600px] h-[600px] pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 60%)" }}
        />
        <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={ins.heroEyebrow} title={ins.heroTitle} sub={ins.heroSub} />
        </div>
      </section>

      {/* Search + Category filter */}
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 mb-10 flex flex-col lg:flex-row lg:items-center gap-5 lg:justify-between">
        {categories.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`insights-filter-${c}`}
                onClick={() => setCategory(c)}
                className={`font-mono-tech text-[11px] uppercase tracking-widest px-4 py-2.5 border transition-colors ${
                  category === c
                    ? "border-cyan-precision text-cyan-precision bg-cyan-precision/5"
                    : "border-white/10 text-[#94A3B8] hover:text-[#F0F4F8]"
                }`}
              >
                {c === "all" ? ins.all : c}
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <div className="relative w-full lg:w-80">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            data-testid="insights-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={ins.searchPlaceholder}
            className="w-full bg-transparent border border-white/10 text-[#F0F4F8] placeholder:text-[#94A3B8]/50 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-cyan-precision transition-colors"
          />
        </div>
      </div>

      <section className="pb-12">
        <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
          {loading ? (
            <p className="text-[#94A3B8] py-16">…</p>
          ) : filtered.length === 0 ? (
            <p data-testid="insights-empty" className="text-[#94A3B8] py-16">
              {articles.length === 0 ? ins.empty : ins.noResults}
            </p>
          ) : (
            <>
              {/* Featured */}
              {featured && (
                <Reveal>
                  <Link
                    to={`/insights/${featured.slug}`}
                    data-testid={`insight-featured-${featured.slug}`}
                    className="group grid grid-cols-1 lg:grid-cols-2 gap-8 border border-white/10 bg-navy-surface/40 overflow-hidden hover:border-cyan-precision/40 transition-colors duration-300 mb-8"
                  >
                    <div className="relative h-64 lg:h-full min-h-[320px] overflow-hidden">
                      <img
                        src={featured.cover}
                        alt={featured.title[lang]}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-4 font-mono-tech text-[11px] uppercase tracking-widest text-cyan-precision">
                        <span>{featured.category}</span>
                        <span className="text-[#94A3B8]/40">·</span>
                        <span className="flex items-center gap-1.5 text-[#94A3B8]">
                          <Clock size={12} /> {featured.read_minutes} {ins.minRead}
                        </span>
                      </div>
                      <h2 className="mt-5 font-display text-3xl lg:text-4xl font-light tracking-tight text-[#F0F4F8] leading-tight">
                        {featured.title[lang]}
                      </h2>
                      <p className="mt-4 text-[#94A3B8] leading-relaxed">{featured.excerpt[lang]}</p>
                      <span className="mt-6 inline-flex items-center gap-1.5 font-mono-tech text-[12px] uppercase tracking-widest text-cyan-precision">
                        {ins.readMore} <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              )}

              {/* Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((a, i) => (
                  <Reveal key={a.slug} delay={i * 0.06}>
                    <Link
                      to={`/insights/${a.slug}`}
                      data-testid={`insight-card-${a.slug}`}
                      className="group flex flex-col h-full border border-white/10 bg-navy-surface/40 overflow-hidden hover:border-cyan-precision/40 hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={a.cover}
                          alt={a.title[lang]}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-6 flex flex-col flex-1">
                        <div className="flex items-center gap-3 font-mono-tech text-[10px] uppercase tracking-widest text-cyan-precision">
                          <span>{a.category}</span>
                          <span className="text-[#94A3B8]/40">·</span>
                          <span className="text-[#94A3B8]">{a.read_minutes} {ins.minRead}</span>
                        </div>
                        <h3 className="mt-4 font-display text-xl font-light text-[#F0F4F8] leading-snug">
                          {a.title[lang]}
                        </h3>
                        <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed line-clamp-3">
                          {a.excerpt[lang]}
                        </p>
                      </div>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CTASection title={ins.ctaTitle} body={ins.ctaBody} btn={ins.ctaBtn} />
    </div>
  );
}
